import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of } from 'rxjs';
import { switchMap, map, catchError, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import * as moment from 'moment';

import { Study } from 'src/app/models/study';
import * as umStudyActions from './um-study.actions';
import { AuthErrorResponse } from 'src/app/models/auth-error-response';
import { setStudyStatus } from '../../studies/helpers/status-helper';
import { ResearcherService } from '../../services/researcher/researcher.service';
import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';

@Injectable()
export class ResearcherStudyEffects {
  getResearcherStudies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umStudyActions.getResearcherStudies),
      switchMap(({ researcherId }) => {
        return this.umResearchersService.getResearcherStudies(researcherId).pipe(
          map((studies) => {
            const transformedStudies = [];
            for (const study of studies) {
              const transformedStudy: Study = this.dashboardCamelCasePipe.keysToCamelCase(study);
              if (transformedStudy.updatedAt) {
                transformedStudy.updatedAt = moment(transformedStudy.updatedAt).format('DD/MM/YYYY');
              }
              if (transformedStudy.pivot) {
                transformedStudy.pivot.blocked = !!transformedStudy.pivot.blocked;
              }
              if (!transformedStudy.pivot || (transformedStudy.pivot && !transformedStudy.pivot.blocked)) {
                transformedStudy.status = setStudyStatus(transformedStudy.startsAt, transformedStudy.endsAt);
              }
              transformedStudies.push(transformedStudy);
            }
            return umStudyActions.getResearcherStudiesSuccess({
              studies: transformedStudies,
            });
          }),
          catchError((err: AuthErrorResponse) => of(umStudyActions.getResearcherStudiesError({ err })))
        );
      })
    )
  );

  getStudiesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umStudyActions.getResearcherStudiesError),
        tap(({ err }) => this.toastrService.error(err.error.error))
      ),
    { dispatch: false }
  );

  changeUmResearcherStudyAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umStudyActions.changeUmResearcherStudyAccess),
      switchMap(({ researcher, study, block }) => {
        return this.umResearchersService.changeResearcherStudyAccess(researcher.id, study.id, !block).pipe(
          map(() => {
            const changedStudy = {
              ...study,
              pivot: { ...study.pivot, blocked: !study.pivot.blocked },
            };
            return umStudyActions.changeUmResearcherStudyAccessSuccess({
              changedStudy,
            });
          }),
          catchError(() => of(umStudyActions.changeUmResearcherStudyAccessError()))
        );
      })
    )
  );

  changeUmResearcherStudyAccessSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umStudyActions.changeUmResearcherStudyAccessSuccess),
        tap(({ changedStudy }) => {
          if (changedStudy.pivot.blocked) {
            this.toastrService.success('Successfully blocked researcher from study');
          } else {
            this.toastrService.success('Successfully unblocked researcher on study');
          }
        })
      ),
    { dispatch: false }
  );

  changeUmResearcherStudyAccessError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umStudyActions.changeUmResearcherStudyAccessError),
        tap(() => this.toastrService.error('Unable to change Researcher access to study'))
      ),
    { dispatch: false }
  );

  deleteStudy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umStudyActions.deleteStudy),
      switchMap(({ study }) => {
        return this.umResearchersService.deleteStudy(study.id).pipe(
          map(() => umStudyActions.deleteStudySuccess({ deletedStudy: study })),
          catchError(() => of(umStudyActions.deleteStudyError()))
        );
      })
    )
  );

  deleteStudySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umStudyActions.deleteStudySuccess),
        tap(() => this.toastrService.success('Study successfully deleted'))
      ),
    { dispatch: false }
  );

  deleteStudyError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umStudyActions.deleteStudyError),
        tap(() => this.toastrService.error('Unable to delete study'))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private toastrService: ToastrService,
    private umResearchersService: ResearcherService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe
  ) {}
}
